import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';

/**
 * API configuration constants for Work Order API
 */
const API_CONFIG = {
  BASE_URL: '/v2/workorders',
  TIMEOUT: 15 * 1000, // 15 seconds
  ERROR_MESSAGES: {
    FETCH_WORK_ORDER_ERROR: 'Unable to fetch work order details.',
    GENERIC_ERROR: 'Unable to process work order request.',
    NETWORK_ERROR: 'Network error occurred. Please check your connection.',
  },
} as const;

/**
 * Generic object type for API responses
 */
interface ApiResponse<T = unknown> {
  [key: string]: T;
}

/**
 * Custom error class for API-related errors
 */
class ApiError extends Error {
  public readonly statusCode?: number;

  public readonly originalError?: unknown;

  constructor(message: string, statusCode?: number, originalError?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.statusCode = statusCode;
    this.originalError = originalError;
  }
}

/**
 * Creates and returns a configured Axios instance for work order API
 * @returns Configured Axios instance
 */
function getWorkOrderApiInstance(): AxiosInstance {
  return axios.create({
    baseURL: API_CONFIG.BASE_URL,
    timeout: API_CONFIG.TIMEOUT,
    timeoutErrorMessage: API_CONFIG.ERROR_MESSAGES.GENERIC_ERROR,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-User-Id': window?.context?.id || 0,
      'X-Group-Id': window?.context?.group?.id || 0,
      'X-Company-Id': window?.context?.company?.id || 0,
    },
  });
}

/**
 * Handles and transforms various error types into ApiError instances
 * @param error - The caught error
 * @returns Formatted ApiError instance
 */
function handleApiError(error: any): ApiError {
  if (error instanceof ApiError) {
    return error;
  }

  if (axios.isAxiosError(error)) {
    if (error?.response?.data) {
      const data = error.response.data as any;
      if (data.errors && Array.isArray(data.errors)) {
        const message = data.errors
          .map((e: any) => (typeof e === 'string' ? e : e.message || JSON.stringify(e)))
          .join(', ');
        return new ApiError(message, error.response.status, error);
      }
    }

    // Handle network errors (timeout, connection issues)
    if (!error.response) {
      return new ApiError(API_CONFIG.ERROR_MESSAGES.NETWORK_ERROR, 0, error);
    }

    const errorMessage = error.config?.url?.includes('workorders')
      ? API_CONFIG.ERROR_MESSAGES.FETCH_WORK_ORDER_ERROR
      : API_CONFIG.ERROR_MESSAGES.GENERIC_ERROR;

    return new ApiError(errorMessage, error.response.status, error);
  }

  return new ApiError(API_CONFIG.ERROR_MESSAGES.GENERIC_ERROR, 0, error);
}

/**
 * Makes an API request with proper error handling and type safety
 * @param options - Axios request configuration
 * @returns Promise resolving to the API response data
 * @throws ApiError when the request fails
 */
export default async function makeWorkOrderApiRequest<T = ApiResponse>(
  options: AxiosRequestConfig,
): Promise<T> {
  try {
    const apiInstance = getWorkOrderApiInstance();
    const response = await apiInstance.request<T>(options);

    if (!response.data) {
      throw new ApiError(
        API_CONFIG.ERROR_MESSAGES.GENERIC_ERROR,
        response.status,
        response,
      );
    }

    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}
